<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    location="center"
    :disabled="disabled"
  >
    <template v-slot:activator="{ props }">
      <v-text-field
        v-model="formattedDate"
        :class="customClass"
        :density="density"
        :label="label"
        :rules="rules"
        :variant="variant"
        :placeholder="placeholder"
        :disabled="disabled"
        hide-details="auto"
        prepend-inner-icon="mdi-calendar"
        readonly
        v-bind="props"
        clearable
        @click:clear="clearDate"
      />
    </template>
    <v-card class="shadow-lg" elevation="12" :width="xs ? width - 30 : 'auto'">
      <div class="position-relative">
        <v-date-picker
          v-model="internalModel"
          :color="color"
          :width="xs ? width - 30 : 'auto'"
          height="auto"
          show-adjacent-months
          title=""
        />
        <v-btn
          class="position-absolute top-0 right-0"
          color="white"
          icon="mdi-close"
          size="small"
          variant="text"
          @click="menu = false"
        ></v-btn>
      </div>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="confirmDate">
          OK
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>
<script setup>
import { onMounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps({
  modelValue: {
    type: [Date, String],
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: 'primary',
  },
  customClass: {
    type: String,
    default: '',
  },
  rules: {
    type: Array,
    default: () => [],
  },
  variant: {
    type: String,
    default: 'outlined',
  },
  density: {
    type: String,
    default: 'compact',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  format: {
    type: String,
    default: 'MM-DD-YYYY', // Default format
  },
  placeholder: {
    type: String,
    default: 'Select date',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { width, height, xs } = useDisplay();
const menu = ref(false);
const internalModel = ref(null);
const selectedDate = ref(null);
const formattedDate = ref('');
const skipNextInternalModelWatch = ref(false);

watch(
  () => props.modelValue,
  (newVal) => {
    skipNextInternalModelWatch.value = true;

    if (!newVal) {
      internalModel.value = null;
      selectedDate.value = null;
      formattedDate.value = '';
      return;
    }

    try {
      const dateValue = newVal instanceof Date ? newVal : new Date(newVal);
      internalModel.value = dateValue;
      selectedDate.value = dateValue;
      formattedDate.value = formatDate(dateValue);
    } catch (e) {
      console.error('Error parsing date:', e);
      internalModel.value = null;
      selectedDate.value = null;
      formattedDate.value = '';
    }
  },
  { immediate: true }
);

watch(internalModel, (newVal) => {
  if (skipNextInternalModelWatch.value) {
    skipNextInternalModelWatch.value = false;
    return;
  }
  
  if (newVal) {
    selectedDate.value = new Date(newVal);
  } else {
    selectedDate.value = null;
  }
});

onMounted(() => {
  if (props.modelValue) {
    try {
      const dateValue = props.modelValue instanceof Date 
        ? props.modelValue 
        : new Date(props.modelValue);
      
      internalModel.value = dateValue;
      selectedDate.value = dateValue;
      formattedDate.value = formatDate(dateValue);
    } catch (e) {
      console.error('Error parsing initial date:', e);
    }
  }
});

function formatDate(inputDate) {
  if (!inputDate) return '';

  try {
    const parsedDate =
      inputDate instanceof Date ? inputDate : new Date(inputDate);
    if (isNaN(parsedDate.getTime())) return '';

    if (props.format === 'M-DD-YYYY') {
      return `${parsedDate.getMonth() + 1}-${String(
        parsedDate.getDate()
      ).padStart(2, '0')}-${parsedDate.getFullYear()}`;
    } else if (props.format === 'MM-DD-YYYY') {
      return `${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(
        parsedDate.getDate()
      ).padStart(2, '0')}-${parsedDate.getFullYear()}`;
    } else {
      // Default format
      return `${String(parsedDate.getDate()).padStart(2, '0')}/${String(
        parsedDate.getMonth() + 1
      ).padStart(2, '0')}/${parsedDate.getFullYear()}`;
    }
  } catch (e) {
    console.error('Error formatting date:', e);
    return '';
  }
}

function confirmDate() {
  if (selectedDate.value) {
    formattedDate.value = formatDate(selectedDate.value);
    emit('update:modelValue', selectedDate.value);
    emit('change', selectedDate.value);
  } else if (internalModel.value) {
    // fallback 
    const dateValue = new Date(internalModel.value);
    formattedDate.value = formatDate(dateValue);
    emit('update:modelValue', dateValue);
    emit('change', dateValue);
  } else {
    emit('update:modelValue', null);
    emit('change', null);
  }
  
  menu.value = false;
}

function clearDate() {
  internalModel.value = null;
  selectedDate.value = null;
  formattedDate.value = '';
  emit('update:modelValue', null);
  emit('change', null);
}
</script>

<style>
.v-overlay__content:has(> .v-date-picker) {
  min-width: auto !important;
  z-index: 3000 !important;
}

.v-overlay--active {
  z-index: 3000 !important;
}

.v-picker-title {
  padding: 0 !important;
}

.position-absolute {
  position: absolute !important;
}

.position-relative {
  position: relative !important;
}

.top-0 {
  top: 0 !important;
}

.right-0 {
  right: 0 !important;
}

@media only screen and (max-width: 600px) {
  .v-overlay__content:has(> .v-date-picker) {
    left: 0 !important;
  }
}
</style>